import { TextInput, ActionIcon, Box } from '@mantine/core';
import { IconChevronUp, IconChevronDown } from '@tabler/icons-react';

// Common cooking fractions
const COOKING_FRACTIONS = [1/8, 1/4, 1/3, 3/8, 1/2, 5/8, 2/3, 3/4, 7/8, 1];

// Helper to get the closest fraction step
const getClosestFractionStep = (current: number, direction: 1 | -1): number => {
  const sorted = [...COOKING_FRACTIONS].sort((a, b) => a - b);
  if (direction === 1) {
    // Next larger fraction
    return sorted.find((f) => f > (current % 1) + 1e-6) ?? 1;
  } else {
    // Next smaller fraction
    return [...sorted].reverse().find((f) => f < (current % 1) - 1e-6) ?? 0;
  }
};

// Helper to convert decimal to fraction representation
const decimalToFraction = (decimal: number): { numerator: number; denominator: number } | null => {
  // Helper function to find greatest common divisor
  const gcd = (a: number, b: number): number => b === 0 ? a : gcd(b, a % b);

  // For cooking fractions, we only care about denominators: 8, 4, 3, 2
  const denominators = [8, 4, 3, 2];

  for (const denom of denominators) {
    const num = Math.round(decimal * denom);
    if (Math.abs(decimal - num / denom) < 0.01) {
      // Simplify the fraction
      const divisor = gcd(num, denom);
      return {
        numerator: num / divisor,
        denominator: denom / divisor
      };
    }
  }

  return null; // No simple fraction found
};

// Helper to display number as fraction
export const formatAsFraction = (value: number): string => {
  if (!value) return '';

  const whole = Math.floor(value);
  const fractionalPart = value - whole;

  // If it's essentially a whole number
  if (Math.abs(fractionalPart) < 0.01) {
    return whole.toString();
  }

  // Convert fractional part to fraction
  const fraction = decimalToFraction(fractionalPart);

  if (fraction) {
    const fractionStr = `${fraction.numerator}/${fraction.denominator}`;
    return whole === 0 ? fractionStr : `${whole} ${fractionStr}`;
  }

  // Fallback to decimal for unusual fractions
  return value.toString();
};

// Helper to round input to nearest valid cooking fraction
export const roundToValidCookingFraction = (value: number): number => {
  if (value <= 0) return 0.125; // Minimum 1/8

  const whole = Math.floor(value);
  const fraction = value - whole;

  // If the fractional part is very small (essentially zero), keep it as a whole number
  if (Math.abs(fraction) < 0.01) {
    return whole;
  }

  // Find closest valid fraction (including 0 for whole numbers)
  const validFractions = [0, ...COOKING_FRACTIONS];
  let closestFraction = validFractions[0];
  let minDistance = Math.abs(fraction - closestFraction);

  for (const validFraction of validFractions) {
    const distance = Math.abs(fraction - validFraction);
    if (distance < minDistance) {
      minDistance = distance;
      closestFraction = validFraction;
    }
  }

  return whole + closestFraction;
};

interface FractionQuantityInputProps {
  value: number;
  onChange: (value: number) => void;
  localInput: string;
  onLocalInputChange: (value: string) => void;
  label?: string;
  variant?: 'default' | 'unstyled';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  width?: number | string;
  required?: boolean;
}

export const FractionQuantityInput = ({
  value,
  onChange,
  localInput,
  onLocalInputChange,
  label = "Quantity",
  variant = 'default',
  size = 'sm',
  width = 100,
  required = false,
}: FractionQuantityInputProps) => {
  const handleIncrement = () => {
    const currentValue = value || 0;
    const whole = Math.floor(currentValue);
    const fraction = currentValue - whole;
    const nextFraction = getClosestFractionStep(fraction, 1);
    const newValue = whole + nextFraction;
    onChange(newValue);
    onLocalInputChange(''); // Clear local input to show formatted fraction
  };

  const handleDecrement = () => {
    const currentValue = value || 0;
    if (currentValue <= 0.125) return; // Don't go below 1/8

    const whole = Math.floor(currentValue);
    const fraction = currentValue - whole;

    // Find the index of current fraction in our array
    const currentFractionIndex = COOKING_FRACTIONS.findIndex(f => Math.abs(f - fraction) < 1e-6);

    let newValue;
    if (currentFractionIndex > 0) {
      // Step to previous fraction within same whole number
      newValue = whole + COOKING_FRACTIONS[currentFractionIndex - 1];
    } else if (currentFractionIndex === 0 && whole > 1) {
      // We're at the first fraction (1/8) and above 1, go to previous whole number
      newValue = whole; // Go to exact whole number (like 1.0)
    } else if (Math.abs(fraction) < 1e-6 && whole > 0) {
      // We're at a whole number (fraction = 0), go to previous whole + largest fraction
      newValue = (whole - 1) + COOKING_FRACTIONS[COOKING_FRACTIONS.length - 2]; // 7/8
    } else {
      // Fallback - find closest smaller fraction
      const prevFraction = getClosestFractionStep(fraction, -1);
      newValue = whole + prevFraction;
    }

    // Don't allow going below 1/8
    newValue = Math.max(0.125, newValue);
    onChange(newValue);
    onLocalInputChange(''); // Clear local input to show formatted fraction
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    // When user finishes typing, round to nearest cooking fraction and format
    const inputValue = event.currentTarget.value;
    let numericValue = 0;

    // Parse the input value
    const fractionMatch = inputValue.match(/^(\d+)?\s*(\d+)\/(\d+)$/);
    if (fractionMatch) {
      const whole = parseInt(fractionMatch[1] || '0');
      const numerator = parseInt(fractionMatch[2]);
      const denominator = parseInt(fractionMatch[3]);
      numericValue = whole + numerator / denominator;
    } else {
      numericValue = parseFloat(inputValue) || 0;
    }

    // Round to valid cooking fraction
    const roundedValue = roundToValidCookingFraction(numericValue);
    onChange(roundedValue);
    onLocalInputChange(''); // Clear local input to show formatted fraction
  };

  return (
    <TextInput
      label={label}
      variant={variant}
      value={localInput || formatAsFraction(value)}
      onChange={(event) => onLocalInputChange(event.currentTarget.value)}
      onBlur={handleBlur}
      onFocus={(event) => event.currentTarget.select()}
      style={{ width }}
      size={size}
      required={required}
      rightSection={
        <Box style={{ display: 'flex', flexDirection: 'column', gap: 1, padding: '2px', marginRight: '2px' }}>
          <ActionIcon
            size="xs"
            variant="subtle"
            color="gray"
            style={{ height: '14px', minHeight: '14px' }}
            onClick={handleIncrement}
          >
            <IconChevronUp size={12} />
          </ActionIcon>
          <ActionIcon
            size="xs"
            variant="subtle"
            color="gray"
            style={{ height: '14px', minHeight: '14px' }}
            onClick={handleDecrement}
          >
            <IconChevronDown size={12} />
          </ActionIcon>
        </Box>
      }
    />
  );
};
